package com.bulut.mecitogullari.core.util

import com.bulut.mecitogullari.core.i18n.Localization
import kotlinx.datetime.LocalDate
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.number
import kotlinx.datetime.toInstant
import kotlinx.datetime.toLocalDateTime
import kotlin.time.Clock
import kotlin.time.ExperimentalTime
import kotlin.time.Instant

/**
 * All transaction dates are stored as epoch millis at UTC midnight of the
 * chosen calendar day (this is also what the Material3 DatePicker returns),
 * so date math below deliberately uses [TimeZone.UTC].
 */

/** A calendar month, the unit of the contractor's accounting workflow. */
data class YearMonth(val year: Int, val month: Int) : Comparable<YearMonth> {

    init {
        require(month in 1..12) { "month must be 1..12" }
    }

    val label: String get() = "${Localization.strings.monthNames[month - 1]} $year"

    fun previous(): YearMonth =
        if (month == 1) YearMonth(year - 1, 12) else YearMonth(year, month - 1)

    fun next(): YearMonth =
        if (month == 12) YearMonth(year + 1, 1) else YearMonth(year, month + 1)

    /** Epoch millis of the first instant of this month (UTC). */
    @OptIn(ExperimentalTime::class)
    fun startEpochMillis(): Long =
        LocalDateTime(year, month, 1, 0, 0).toInstant(TimeZone.UTC).toEpochMilliseconds()

    /** Epoch millis of the first instant of the next month (UTC, exclusive end). */
    fun endEpochMillisExclusive(): Long = next().startEpochMillis()

    operator fun contains(epochMillis: Long): Boolean =
        epochMillis >= startEpochMillis() && epochMillis < endEpochMillisExclusive()

    override fun compareTo(other: YearMonth): Int =
        compareValuesBy(this, other, { it.year }, { it.month })
}

@OptIn(ExperimentalTime::class)
fun todayLocalDate(): LocalDate =
    Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).date

fun currentYearMonth(): YearMonth = todayLocalDate().let { YearMonth(it.year, it.month.number) }

@OptIn(ExperimentalTime::class)
fun LocalDate.toUtcMidnightMillis(): Long =
    LocalDateTime(year, month.number, day, 0, 0)
        .toInstant(TimeZone.UTC)
        .toEpochMilliseconds()

fun todayUtcMidnightMillis(): Long = todayLocalDate().toUtcMidnightMillis()

@OptIn(ExperimentalTime::class)
fun epochMillisToLocalDate(epochMillis: Long): LocalDate =
    Instant.fromEpochMilliseconds(epochMillis).toLocalDateTime(TimeZone.UTC).date

fun yearMonthOf(epochMillis: Long): YearMonth =
    epochMillisToLocalDate(epochMillis).let { YearMonth(it.year, it.month.number) }

/** "05.07.2026" */
fun formatDate(epochMillis: Long): String {
    val d = epochMillisToLocalDate(epochMillis)
    val day = d.day.toString().padStart(2, '0')
    val month = d.month.number.toString().padStart(2, '0')
    return "$day.$month.${d.year}"
}

/** "5 Tem" — compact form for list rows. */
fun formatDateShort(epochMillis: Long): String {
    val d = epochMillisToLocalDate(epochMillis)
    return "${d.day} ${Localization.strings.monthNamesShort[d.month.number - 1]}"
}

/** "Temmuz" for month 7 (localized). */
fun monthName(month: Int): String = Localization.strings.monthNames[month - 1]

fun daysInMonth(month: Int, year: Int): Int = when (month) {
    1, 3, 5, 7, 8, 10, 12 -> 31
    4, 6, 9, 11 -> 30
    else -> if (year % 4 == 0 && (year % 100 != 0 || year % 400 == 0)) 29 else 28
}
